import { registerBrick } from 'olympe';
import {ReactBrick, useProperty} from "@olympeio/core";
import {combineLatest} from "rxjs";
import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Card, CardHeader, CardContent, Typography, Box, TextField, ClickAwayListener } from '@mui/material';


export default class BilanCategDisplay extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false)
        ]);
    }
    /**
     * @override
     */
    static getReactComponent($) {

        return () => {
            const [categories, setCategories] = useState();
            const [editingItem, setEditingItem] = useState(null);
            const [editValue, setEditValue] = useState('');
            const data = useProperty($, 'data', true);

            useEffect(() => {
                setCategories(data);
            }, [data])


            const handleDragEnd = (result) => {
                if (!result.destination) return;

                const { source, destination } = result;
                const [sourceCategory, sourceSubcategory] = source.droppableId.split('#');
                const [destCategory, destSubcategory] = destination.droppableId.split('#');

                const newData = { ...categories };

                // Get the item being dragged
                const [movedItem] = newData[sourceCategory][sourceSubcategory].splice(source.index, 1);

                // Add the item to its new location
                newData[destCategory][destSubcategory].splice(destination.index, 0, movedItem);

                $.set('data', newData);
            };

            const handleValueClick = (categoryKey, subcategoryKey, index, value) => {
                setEditingItem({ categoryKey, subcategoryKey, index });
                setEditValue(value.toString());
            };

            const handleValueChange = (e) => {
                setEditValue(e.target.value);
            };

            const handleValueSubmit = () => {
                if (!editingItem) return;

                const { categoryKey, subcategoryKey, index } = editingItem;
                const newValue = parseFloat(editValue);

                if (isNaN(newValue)) {
                    setEditingItem(null);
                    setEditValue('');
                    return;
                }

                const newData = { ...categories };
                newData[categoryKey][subcategoryKey][index] = {
                    ...newData[categoryKey][subcategoryKey][index],
                    value: newValue
                };

                $.set('data', newData);
                setEditingItem(null);
                setEditValue('');
            };

            const renderItem = (item, index, categoryKey, subcategoryKey) => (
                <Draggable
                    key={`${categoryKey}#${subcategoryKey}#${index}`}
                    draggableId={`${categoryKey}#${subcategoryKey}#${index}`}
                    index={index}
                >
                    {(provided, snapshot) => {
                        if (snapshot.isDragging) {
                            const offset = { x: $.get('X') + $.get('abs X'), y: $.get('Y') + $.get('abs Y') }
                            const x = provided.draggableProps.style.left - offset.x;
                            const y = provided.draggableProps.style.top - offset.y;
                            provided.draggableProps.style.left = x;
                            provided.draggableProps.style.top = y;
                        }


                        const isEditing = editingItem &&
                            editingItem.categoryKey === categoryKey &&
                            editingItem.subcategoryKey === subcategoryKey &&
                            editingItem.index === index;

                        return (<Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                                p: 2,
                                mb: 1,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                boxShadow: snapshot.isDragging ? 3 : 1,
                                '&:hover': {
                                    boxShadow: 2,
                                },
                                display: "flex",
                                justifyContent: "space-between"
                            }}
                        >
                            <Typography variant="body2">{item.description}</Typography>
                            {isEditing ? (
                                <ClickAwayListener onClickAway={handleValueSubmit}>
                                    <TextField
                                        value={editValue}
                                        onChange={handleValueChange}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleValueSubmit();
                                            }
                                        }}
                                        size="small"
                                        autoFocus
                                        sx={{ width: '100px' }}
                                    />
                                </ClickAwayListener>
                            ) : (
                                <Typography
                                    variant="body2"
                                    onClick={() => handleValueClick(categoryKey, subcategoryKey, index, item.value)}
                                    sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                >
                                    {item.value.toFixed(2)}
                                </Typography>
                            )}
                        </Box>)
                    }}
                </Draggable>
            );

            return (
                <Box sx={{ maxWidth: '100%',
                    height: useProperty($, 'Height', true) + 'px',
                    maxHeight: useProperty($, 'Height', true) + 'px',
                    display: 'flex',
                    flexDirection: 'column',
                    m: 2 }}>
                    <CardHeader title="Financial Categories" />
                    <CardContent sx={{
                        overflowY: 'auto',
                        flexGrow: 1,
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'grey.100',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'grey.400',
                            borderRadius: '4px',
                        },
                    }}>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
                                {categories && Object.entries(categories).map(([categoryKey, subcategories]) => (
                                    <Card key={categoryKey} sx={{ bgcolor: 'grey.50', p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                                            {categoryKey.replace(/-/g, ' ').toUpperCase()}
                                        </Typography>
                                        {Object.entries(subcategories).map(([subcategoryKey, items]) => (
                                            <Box key={subcategoryKey} sx={{ mb: 3 }}>
                                                <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary' }}>
                                                    {subcategoryKey.replace(/-/g, ' ')}
                                                </Typography>
                                                <Droppable droppableId={`${categoryKey}#${subcategoryKey}`}>
                                                    {(provided, snapshot) => (
                                                        <Box
                                                            ref={provided.innerRef}
                                                            {...provided.droppableProps}
                                                            sx={{
                                                                minHeight: 50,
                                                                p: 1,
                                                                bgcolor: snapshot.isDraggingOver ? 'grey.100' : 'transparent',
                                                                borderRadius: 1,
                                                                transition: 'background-color 0.2s ease',
                                                            }}
                                                        >
                                                            {items.map((item, index) =>
                                                                renderItem(item, index, categoryKey, subcategoryKey)
                                                            )}
                                                            {provided.placeholder}
                                                        </Box>
                                                    )}
                                                </Droppable>
                                            </Box>
                                        ))}
                                    </Card>
                                ))}
                            </Box>
                        </DragDropContext>
                    </CardContent>
                </Box>
            );
        }
    }
}

registerBrick('01930cd3b68746cc47fa', BilanCategDisplay);
